// API 配置文件 - 前端与后端通信
const API_BASE_URL = import.meta.env.VITE_API_BASE_URL || '/api';

// 通用 API 请求函数
async function apiRequest(endpoint, options = {}) {
  const url = `${API_BASE_URL}${endpoint}`;
  const token = sessionStorage.getItem('authToken');
  const config = {
    headers: {
      'Content-Type': 'application/json',
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...options.headers,
    },
    ...options,
  };

  try {
    const response = await fetch(url, config);

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({ message: response.statusText }));

      // 账号被禁用：清除 token 并强制退出登录
      if (response.status === 403 && errorData.code === 'ACCOUNT_DISABLED') {
        sessionStorage.removeItem('authToken');
        sessionStorage.removeItem('user');
        localStorage.removeItem('currentStudent');
        alert('您的账号已被禁用，请联系管理员。');
        window.location.reload();
        return;
      }

      // 【新需求109】把 HTTP 状态码与后端错误码附加到 Error 对象上。
      //   原先只抛出 message，上层想区分"越权(403)"和"网络故障"只能对中文文案做
      //   正则匹配—— 文案一改判断就失效。附加结构化字段后调用方可精确判断，
      //   这是"学生端一片空白但看不出原因"那个问题能被快速定位的前提。
      const error = new Error(errorData.message || `HTTP error! status: ${response.status}`);
      error.status = response.status;
      error.code = errorData.code || '';
      throw error;
    }

    // 处理 204 No Content 响应
    if (response.status === 204) {
      return null;
    }

    const result = await response.json();

    // 后端返回格式: { success: true, data: [...] }
    // 提取 data 字段，如果不存在则返回整个结果
    return result.data !== undefined ? result.data : result;
  } catch (error) {
    console.error(`API请求失败: ${endpoint}`, error);
    throw error;
  }
}

// 学校 API
export const schoolsAPI = {
  getByStudent: async (studentId) => {
    return await apiRequest(`/schools/student/${studentId}`);
  },
  // 获取全局学校报考统计（仪表盘使用）
  getStats: async ({ teacherId } = {}) => {
    const params = new URLSearchParams();
    if (teacherId) params.append('teacher_id', teacherId);
    const qs = params.toString();
    return await apiRequest(`/schools/stats${qs ? '?' + qs : ''}`);
  },
  // 获取全局事件统计（仪表盘使用）
  getEventStats: async ({ teacherId } = {}) => {
    const params = new URLSearchParams();
    if (teacherId) params.append('teacher_id', teacherId);
    const qs = params.toString();
    return await apiRequest(`/schools/stats/events${qs ? '?' + qs : ''}`);
  },
  create: async (schoolData) => {
    return await apiRequest('/schools', {
      method: 'POST',
      body: JSON.stringify(schoolData),
    });
  },
  update: async (schoolId, schoolData) => {
    return await apiRequest(`/schools/${schoolId}`, {
      method: 'PUT',
      body: JSON.stringify(schoolData),
    });
  },
  // 【新需求112 第1项】一键更新申请状态（只动 status 列，不重建材料/事件）
  // 需求58：用 PUT 而非 PATCH，避免 Cloudflare Pages CDN 预检不放 PATCH
  updateStatus: async (schoolId, status) => {
    return await apiRequest(`/schools/${schoolId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ status }),
    });
  },
  delete: async (schoolId) => {
    return await apiRequest(`/schools/${schoolId}`, {
      method: 'DELETE',
    });
  },
};

// 事件 API
export const eventsAPI = {
  getByStudent: async (studentId) => {
    return await apiRequest(`/events/student/${studentId}`);
  },
  create: async (eventData) => {
    return await apiRequest('/events', {
      method: 'POST',
      body: JSON.stringify(eventData),
    });
  },
  update: async (eventId, eventData) => {
    return await apiRequest(`/events/${eventId}`, {
      method: 'PUT',
      body: JSON.stringify(eventData),
    });
  },
  delete: async (eventId) => {
    return await apiRequest(`/events/${eventId}`, {
      method: 'DELETE',
    });
  },
  // 切换事件完成状态（幂等版）
  // 需求58：使用 PUT 而非 PATCH，避免 Cloudflare Pages CDN 默认预检不放 PATCH 方法导致失败
  toggleComplete: async (eventId) => {
    return await apiRequest(`/events/${eventId}/toggle`, {
      method: 'PUT',
    });
  },
};

// 材料 API
export const materialsAPI = {
  getByStudent: async (studentId) => {
    return await apiRequest(`/materials/student/${studentId}`);
  },
  create: async (materialData) => {
    return await apiRequest('/materials', {
      method: 'POST',
      body: JSON.stringify(materialData),
    });
  },
  update: async (materialId, materialData) => {
    return await apiRequest(`/materials/${materialId}`, {
      method: 'PUT',
      body: JSON.stringify(materialData),
    });
  },
  delete: async (materialId) => {
    return await apiRequest(`/materials/${materialId}`, {
      method: 'DELETE',
    });
  },
  updateStatus: async (materialId, completed, checkedBy) => {
    return await apiRequest(`/materials/${materialId}/status`, {
      method: 'PUT',
      body: JSON.stringify({ completed, checked_by: checkedBy }),
    });
  },
  // 需求58：使用 PUT 而非 PATCH，避免 Cloudflare Pages CDN 默认预检不放 PATCH 方法导致失败
  toggle: async (materialId, checkedBy) => {
    return await apiRequest(`/materials/${materialId}/toggle`, {
      method: 'PUT',
      body: JSON.stringify({ checked_by: checkedBy }),
    });
  },
};

// 学生 API
export const studentsAPI = {
  getAll: async () => {
    return await apiRequest('/students');
  },
  getByTeacher: async (teacherId) => {
    return await apiRequest(`/students/teacher/${teacherId}`);
  },
  getById: async (id) => {
    return await apiRequest(`/students/${id}`);
  },
  // 新需求43：创建学生（可选同时创建登录账号，传 password 即可）
  create: async (data) => {
    return await apiRequest('/students', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return await apiRequest(`/students/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    return await apiRequest(`/students/${id}`, {
      method: 'DELETE',
    });
  },
  transfer: async (id, teacherId) => {
    return await apiRequest(`/students/${id}/transfer`, {
      method: 'PUT',
      body: JSON.stringify({ teacher_id: teacherId }),
    });
  },
  search: async (query, teacherId) => {
    const params = new URLSearchParams();
    if (query) params.append('q', query);
    if (teacherId) params.append('teacher_id', teacherId);
    return await apiRequest(`/students/search/query?${params.toString()}`);
  },
  addNote: async (id, content) => {
    return await apiRequest(`/students/${id}/notes`, {
      method: 'POST',
      body: JSON.stringify({ content }),
    });
  },
  deleteNote: async (id, noteId) => {
    return await apiRequest(`/students/${id}/notes/${noteId}`, {
      method: 'DELETE',
    });
  },
};

// 老师 API
export const teachersAPI = {
  getAll: async () => {
    return await apiRequest('/teachers');
  },
  getById: async (id) => {
    return await apiRequest(`/teachers/${id}`);
  },
  getStudents: async (id) => {
    return await apiRequest(`/teachers/${id}/students`);
  },
  create: async (data) => {
    return await apiRequest('/teachers', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return await apiRequest(`/teachers/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    return await apiRequest(`/teachers/${id}`, {
      method: 'DELETE',
    });
  },
};

// 学校信息库 API（D1 数据库同步）
export const schoolDatabaseAPI = {
  getAll: async (params = {}) => {
    const qs = new URLSearchParams();
    if (params.type) qs.append('type', params.type);
    if (params.search) qs.append('search', params.search);
    const queryStr = qs.toString();
    return await apiRequest(`/school-database${queryStr ? '?' + queryStr : ''}`);
  },
  getById: async (id) => {
    return await apiRequest(`/school-database/${id}`);
  },
  create: async (data) => {
    return await apiRequest('/school-database', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return await apiRequest(`/school-database/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  delete: async (id) => {
    return await apiRequest(`/school-database/${id}`, {
      method: 'DELETE',
    });
  },
  batchImport: async (schools) => {
    return await apiRequest('/school-database/batch', {
      method: 'POST',
      body: JSON.stringify({ schools }),
    });
  },
};

// 用户管理 API（仅管理员）
export const usersAPI = {
  getAll: async () => {
    return await apiRequest('/users');
  },
  delete: async (id) => {
    return await apiRequest(`/users/${id}`, {
      method: 'DELETE',
    });
  },
  // 禁用/启用账号
  toggleActive: async (id) => {
    return await apiRequest(`/users/${id}/toggle-active`, {
      method: 'PUT',
    });
  },
  // 创建管理员账号
  createAdmin: async (data) => {
    return await apiRequest('/users/create-admin', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
};

// 反馈 API
export const feedbackAPI = {
  // 提交反馈（公开，无需登录）
  submit: async (data) => {
    return await apiRequest('/feedback', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  // 管理员查询反馈列表
  getList: async ({ status, type, page = 1, pageSize = 20 } = {}) => {
    const params = new URLSearchParams();
    if (status) params.append('status', status);
    if (type) params.append('type', type);
    params.append('page', page);
    params.append('pageSize', pageSize);
    return await apiRequest(`/feedback?${params.toString()}`);
  },
  // 管理员更新反馈状态
  // 需求58：统一改用 PUT，避免 Cloudflare Pages CDN 默认预检不放 PATCH
  updateStatus: async (id, data) => {
    return await apiRequest(`/feedback/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
};

// ─── 截止日提醒 API ──────────────────────────────────────────────────────────
export const remindersAPI = {
  // 获取截止日提醒（需求57：携带本地日期避免 Worker UTC 时区偏差）
  getToday: async () => {
    const now = new Date();
    const clientDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return await apiRequest(`/reminders/today?clientDate=${clientDate}`);
  },
  // 确认单个提醒（需求57：携带本地日期，服务端用于唯一键去重）
  acknowledge: async (eventId, eventTitle) => {
    const now = new Date();
    const clientDate = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}-${String(now.getDate()).padStart(2, '0')}`;
    return await apiRequest(`/reminders/acknowledge?clientDate=${clientDate}`, {
      method: 'POST',
      body: JSON.stringify({ eventId, eventTitle }),
    });
  },
  // 获取提醒历史
  getHistory: async (studentId) => {
    return await apiRequest(`/reminders/history/${studentId}`);
  },
  // 获取事件的确认状态（用于时间线卡片显示"已确认"）
  getAcknowledged: async (studentId) => {
    return await apiRequest(`/reminders/acknowledged/${studentId}`);
  },  // 获取提醒设置（需求56：老师可传 studentId 读取指定学生的设置）
  getSettings: async (studentId) => {
    const qs = studentId ? `?studentId=${encodeURIComponent(studentId)}` : '';
    return await apiRequest(`/reminders/settings${qs}`);
  },
  // 保存提醒设置（需求56：老师可传 targetStudentIds / applyToAllMyStudents 批量写入）
  saveSettings: async (settings) => {
    return await apiRequest('/reminders/settings', {
      method: 'POST',
      body: JSON.stringify(settings),
    });
  },
};

// ─── 认证 API ────────────────────────────────────────────────────────────────
export const authAPI = {
  // 明学账号登录
  mingxueLogin: async (username, password) => {
    return await apiRequest('/auth/mingxue-login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    });
  },
};

// ─── 塾内备考资料库 API（需求38）──────────────────────────────────────────────
export const studyResourcesAPI = {
  // 列表查询：search, category, is_public（'0'/'1'，仅老师/管理员可过滤）
  list: async ({ search, category, is_public } = {}) => {
    const params = new URLSearchParams();
    if (search) params.append('search', search);
    if (category) params.append('category', category);
    if (is_public === '0' || is_public === '1' || is_public === 0 || is_public === 1) {
      params.append('is_public', String(is_public));
    }
    const qs = params.toString();
    return await apiRequest(`/study-resources${qs ? '?' + qs : ''}`);
  },
  get: async (id) => {
    return await apiRequest(`/study-resources/${id}`);
  },
  create: async (data) => {
    return await apiRequest('/study-resources', {
      method: 'POST',
      body: JSON.stringify(data),
    });
  },
  update: async (id, data) => {
    return await apiRequest(`/study-resources/${id}`, {
      method: 'PUT',
      body: JSON.stringify(data),
    });
  },
  // 快速切换公开/私密
  // 需求58：统一改用 PUT，避免 Cloudflare Pages CDN 默认预检不放 PATCH
  setVisibility: async (id, isPublic) => {
    return await apiRequest(`/study-resources/${id}/visibility`, {
      method: 'PUT',
      body: JSON.stringify({ is_public: !!isPublic }),
    });
  },
  delete: async (id) => {
    return await apiRequest(`/study-resources/${id}`, {
      method: 'DELETE',
    });
  },
};

// ─── 学邦数据同步 API ────────────────────────────────────────────────────────
export const xuebangAPI = {
  // 获取学邦配置状态
  getConfig: async () => {
    return await apiRequest('/xuebang/config');
  },
  // 预览学邦学生数据（不执行同步）
  preview: async () => {
    return await apiRequest('/xuebang/preview');
  },
  // 执行同步
  sync: async ({ selectedIds, defaultTeacherId, defaultPassword } = {}) => {
    return await apiRequest('/xuebang/sync', {
      method: 'POST',
      body: JSON.stringify({ selectedIds, defaultTeacherId, defaultPassword }),
    });
  },
  // 刷新已关联学生的信息
  refresh: async () => {
    return await apiRequest('/xuebang/refresh', {
      method: 'POST',
    });
  },
  // 获取同步历史日志
  getSyncLogs: async () => {
    return await apiRequest('/xuebang/sync-logs');
  },
};

// 【新需求77】站内消息 API
export const messagesAPI = {
  // 列表（分页 + 搜索 + 角色范围 + 是否仅看自己发布的）
  list: async ({ page = 1, pageSize = 20, search = '', audience = '', mine = false, includeRevoked = false } = {}) => {
    const params = new URLSearchParams();
    params.append('page', page);
    params.append('pageSize', pageSize);
    if (search) params.append('search', search);
    if (audience) params.append('audience', audience);
    if (mine) params.append('mine', '1');
    if (includeRevoked) params.append('include_revoked', '1');
    return await apiRequest(`/messages?${params.toString()}`);
  },
  // 时间线顶部横幅（最新未读，最多 5 条）
  banner: async () => {
    return await apiRequest('/messages/banner');
  },
  // 未读数量（角标用）
  unreadCount: async () => {
    return await apiRequest('/messages/unread-count');
  },
  // 详情（GET 时后端会自动标记已读）
  get: async (id) => {
    return await apiRequest(`/messages/${id}`);
  },
  // 创建消息
  create: async (payload) => {
    return await apiRequest('/messages', {
      method: 'POST',
      body: JSON.stringify(payload),
    });
  },
  // 更新消息
  update: async (id, payload) => {
    return await apiRequest(`/messages/${id}`, {
      method: 'PUT',
      body: JSON.stringify(payload),
    });
  },
  // 撤回 / 取消撤回
  revoke: async (id, revoked = true) => {
    return await apiRequest(`/messages/${id}/revoke`, {
      method: 'PUT',
      body: JSON.stringify({ revoked }),
    });
  },
  // 永久删除（admin only）
  remove: async (id) => {
    return await apiRequest(`/messages/${id}`, { method: 'DELETE' });
  },
  // 显式标记已读
  markRead: async (id) => {
    return await apiRequest(`/messages/${id}/read`, { method: 'POST' });
  },
  // 一键全部标记为已读
  markAllRead: async () => {
    return await apiRequest('/messages/read-all', { method: 'POST' });
  },
};

// 【新需求109】每日待办聚合 API
export const todosAPI = {
  /**
   * 一次拿到当前用户可见范围内的全部待办原始数据。
   * 学生 → 只有自己；老师 → 名下学生（有 view_all_students 则全部）；管理员 → 全部。
   * 范围与权限判断都在后端做，前端不做兜底过滤（避免两套口径）。
   * @param {{days?: number, scope?: 'mine'}} [opts] days 默认 60；scope='mine' 让老师强制只看自己负责的
   */
  getAll: async ({ days, scope } = {}) => {
    const params = new URLSearchParams();
    if (days) params.append('days', String(days));
    if (scope) params.append('scope', scope);
    const qs = params.toString();
    return await apiRequest(`/todos${qs ? '?' + qs : ''}`);
  },
};

// 【新需求77】R2 图片上传 API
export const uploadAPI = {
  // 通过 FormData 上传图片，返回 { key, url, size, mime }
  // 注意：此接口走 multipart，不能复用 apiRequest（会强制 Content-Type: application/json）
  uploadImage: async (file) => {
    const url = `${API_BASE_URL}/upload/image`;
    const token = sessionStorage.getItem('authToken');
    const fd = new FormData();
    fd.append('file', file);
    const res = await fetch(url, {
      method: 'POST',
      headers: token ? { 'Authorization': `Bearer ${token}` } : {},
      body: fd,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({ message: res.statusText }));
      throw new Error(err.message || `上传失败 (${res.status})`);
    }
    const result = await res.json();
    return result.data !== undefined ? result.data : result;
  },
};
